import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";
import { FiFileText, FiSend, FiCheckCircle, FiXCircle, FiClock, FiRepeat } from "react-icons/fi";
import * as estimationService from "../../services/estimationService";
import { getRole } from "../../components/Apicall";
import { PillButton, StatTile, StatusBadge, STATUS_TINT, TintButton } from "./EstimationButtons";
import { formatDateHyphenated } from "../../utils/dateFormat";

const STATUS_ICON = {
  DRAFT: FiFileText,
  SENT: FiSend,
  ACCEPTED: FiCheckCircle,
  DECLINED: FiXCircle,
  EXPIRED: FiClock,
  CONVERTED: FiRepeat
};

const PAGE_SIZE = 10;

const thStyle = {
  background: "#F9FAFB",
  fontWeight: 600,
  fontSize: 13,
  color: "#374151",
  padding: "12px 16px",
  borderBottom: "1px solid #E5E7EB",
  whiteSpace: "nowrap",
  textAlign: "left"
};

const tdStyle = {
  padding: "12px 16px",
  fontSize: 14,
  color: "#374151",
  borderBottom: "1px solid #F3F4F6",
  verticalAlign: "middle"
};

// Rebuilt as a plain <table> with inline styles (not react-bootstrap Table)
// to match SubscriptionTable.jsx's exact look, which is itself entirely
// inline-styled - a Bootstrap-class-based table would fight that match.
function Estimations() {
  const navigate = useNavigate();
  const isOwner = getRole() === "3";
  const [loading, setLoading] = useState(true);
  const [allRows, setAllRows] = useState([]);
  const [status, setStatus] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [hoveredRow, setHoveredRow] = useState(null);

  // Fetches the full (search-filtered, status-unfiltered) set so the status
  // tiles can show accurate counts across every status at once - the
  // backend has no separate count endpoint, and this list is small enough
  // per vendor that client-side filtering/pagination is the simplest
  // correct approach without adding a backend change for this alone.
  async function load() {
    setLoading(true);
    try {
      const res = await estimationService.getEstimations({ search });
      setAllRows(res?.data || []);
    } catch {
      toast.error("Failed to load quotations");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    const timer = setTimeout(load, 250);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    setPage(1);
  }, [status, search]);

  const counts = useMemo(() => {
    const c = {};
    for (const r of allRows) c[r.status] = (c[r.status] || 0) + 1;
    return c;
  }, [allRows]);

  const filteredRows = useMemo(
    () => (status ? allRows.filter((r) => r.status === status) : allRows),
    [allRows, status]
  );

  const totalPages = Math.max(1, Math.ceil(filteredRows.length / PAGE_SIZE));
  const pageRows = filteredRows.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const rangeStart = filteredRows.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, filteredRows.length);

  return (
    <div style={{ padding: 24, background: "#F3F4F6", minHeight: "100vh", fontFamily: "Inter, sans-serif" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, gap: 12, flexWrap: "wrap" }}>
        <h1 style={{ fontSize: 24, fontWeight: 700, color: "#111827", margin: 0 }}>Quotations</h1>
        <PillButton onClick={() => navigate("/estimations/new")}>+ New Quotation</PillButton>
      </div>

      <div style={{ display: "flex", flexWrap: "wrap", gap: 16, marginBottom: 24 }}>
        {Object.keys(STATUS_TINT).map((s) => (
          <StatTile
            key={s}
            label={s.charAt(0) + s.slice(1).toLowerCase()}
            value={counts[s] || 0}
            icon={STATUS_ICON[s]}
            tint={STATUS_TINT[s]}
            active={status === s}
            onClick={() => setStatus(status === s ? "" : s)}
          />
        ))}
      </div>

      <div style={{ marginBottom: 16 }}>
        <input
          style={{
            maxWidth: 280,
            border: "1px solid #D1D5DB",
            borderRadius: 8,
            padding: "8px 12px",
            fontSize: 14,
            color: "#374151"
          }}
          placeholder="Search by customer, phone, or #"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div style={{ background: "#fff", borderRadius: 12, boxShadow: "0 1px 4px rgba(0,0,0,0.08)", overflow: "hidden" }}>
        {loading ? (
          <div style={{ textAlign: "center", padding: 48, color: "#6B7280" }}>Loading…</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", background: "#fff" }}>
              <thead>
                <tr>
                  <th style={thStyle}>#</th>
                  <th style={thStyle}>Customer</th>
                  <th style={thStyle}>Valid Until</th>
                  <th style={thStyle}>Status</th>
                  <th style={{ ...thStyle, textAlign: "right" }}>Grand Total</th>
                  <th style={thStyle}></th>
                </tr>
              </thead>
              <tbody>
                {pageRows.length === 0 ? (
                  <tr>
                    <td colSpan={6} style={{ ...tdStyle, textAlign: "center", color: "#6B7280", padding: 48 }}>
                      No quotations found.
                    </td>
                  </tr>
                ) : (
                  pageRows.map((r) => (
                    <tr
                      key={r.id}
                      onClick={() => navigate(`/estimations/${r.id}`)}
                      onMouseEnter={() => setHoveredRow(r.id)}
                      onMouseLeave={() => setHoveredRow(null)}
                      style={{ cursor: "pointer", background: hoveredRow === r.id ? "#F9FAFB" : "transparent", transition: "background 0.15s" }}
                    >
                      <td style={{ ...tdStyle, fontWeight: 600, color: "#111827" }}>
                        {r.display_estimation_id}
                        {r.version > 1 ? ` (v${r.version})` : ""}
                      </td>
                      <td style={{ ...tdStyle, fontWeight: 600, color: "#111827" }}>
                        {r.customer_name}
                        <div style={{ fontWeight: 400, color: "#6B7280", fontSize: 12 }}>{r.phone_number}</div>
                      </td>
                      <td style={tdStyle}>{formatDateHyphenated(r.valid_until)}</td>
                      <td style={tdStyle}>
                        <StatusBadge status={r.status} />
                      </td>
                      <td style={{ ...tdStyle, textAlign: "right", fontWeight: 600, color: "#111827" }}>
                        {r.currency} {Number(r.grand_total).toFixed(2)}
                      </td>
                      <td style={tdStyle} onClick={(e) => e.stopPropagation()}>
                        <div style={{ display: "flex", gap: 8 }}>
                          {r.status === "DRAFT" && (
                            <TintButton tint="blue" onClick={() => navigate(`/estimations/${r.id}/edit`)}>
                              Edit
                            </TintButton>
                          )}
                          {isOwner && r.status === "ACCEPTED" && (
                            <PillButton
                              style={{ padding: "6px 16px", fontSize: 13 }}
                              onClick={() => navigate(`/vendor_bookings?from_estimation=${r.id}`)}
                            >
                              Convert
                            </PillButton>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        )}

        {!loading && filteredRows.length > 0 && (
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "16px", flexWrap: "wrap", gap: 8 }}>
            <span style={{ fontSize: 13, color: "#6B7280" }}>
              Showing {rangeStart}–{rangeEnd} of {filteredRows.length}
            </span>
            <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page === 1}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 13,
                  background: "#fff",
                  color: page === 1 ? "#D1D5DB" : "#374151",
                  cursor: page === 1 ? "not-allowed" : "pointer"
                }}
              >
                Previous
              </button>
              <span style={{ fontSize: 13, color: "#374151" }}>
                Page {page} of {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page === totalPages}
                style={{
                  border: "1px solid #D1D5DB",
                  borderRadius: 8,
                  padding: "6px 12px",
                  fontSize: 13,
                  background: "#fff",
                  color: page === totalPages ? "#D1D5DB" : "#374151",
                  cursor: page === totalPages ? "not-allowed" : "pointer"
                }}
              >
                Next
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default Estimations;
